import { gsap } from 'gsap';
import Vue from 'vue';

Vue.component('cyberster-page-component', {
    data() {
        return {
            isMobile: false,
            timeout: null,
        };
    },

    mounted() {
        this.checkWindowInnerWidth();

        window.addEventListener('resize', this.checkWindowInnerWidth);

        this.animateHero();
        this.animateIntroText();
        this.animateImageWithText();
        this.animateFirstImagesGrid();
        this.animateSecondImageGrid();
        this.animateThirdImageGrid();

        this.timeout = setTimeout(() => {
            this.animateSliderSectionWithFeatures();
        }, 200);
    },

    components: {
        'model-slider-section-with-features': () =>
            import('../async-components/async-slider-section-with-features'),
    },

    destroyed() {
        window.removeEventListener('resize', this.checkWindowInnerWidth);
        clearTimeout(this.timeout);
    },

    methods: {
        checkWindowInnerWidth() {
            this.isMobile = window.innerWidth < 1024;
        },

        animateHero() {
            const timeline = gsap.timeline();
            timeline
                .to('.cyberster-hero__loading-overlay', {
                    top: '100%',
                    delay: 0.5,
                    ease: 'power4.in',
                    duration: 0.8,
                })
                .to('.cyberster-hero__main-content > h1 > strong', {
                    top: 0,
                    duration: 0.4,
                    ease: 'expo.inOut',
                })
                .add('content', 1.8)
                .to(
                    '.cyberster-hero__main-content > p',
                    {
                        opacity: 0.8,
                        duration: 0.5,
                        ease: 'power4.in',
                    },
                    'content',
                )
                .fromTo(
                    '#patches',
                    { opacity: 0 },
                    {
                        opacity: 1,
                        duration: 0.5,
                        ease: 'power4.in',
                    },
                    'content',
                )
                .to(
                    '.cyberster-hero__buttons',
                    {
                        opacity: 1,
                        duration: 0.5,
                        ease: 'power4.in',
                    },
                    'content',
                )
                .to(
                    '.cyberster-hero__label',
                    {
                        opacity: 0.8,
                        duration: 0.5,
                        ease: 'power4.in',
                    },
                    'content',
                );
        },

        animateIntroText() {
            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: '.cyberster-hero__intro-text-container',
                    pin: true,
                    start: 'top top',
                    end: '+=500',
                    scrub: 1.2,
                },
            });

            timeline.to('.cyberster-hero__intro-text', {
                opacity: 1,
                maskImage:
                    'linear-gradient(to bottom, black 100%, transparent)',
                duration: 1,
                ease: 'none',
            });
        },

        animateFirstImagesGrid() {
            const pictures = [];
            const imagesParent = document.getElementsByClassName(
                'first-row-full-image',
            );
            if (!imagesParent.length) return;

            for (const parent of imagesParent) {
                pictures.push(parent.querySelectorAll(':scope > picture'));
            }

            if (this.isMobile) {
                const mobileTimeline = gsap.timeline({
                    scrollTrigger: {
                        trigger: imagesParent,
                        start: 'top center',
                    },
                });

                mobileTimeline
                    .to(
                        '.first-row-full-image > .cyberster-images-grid__text-over-image-container > .cyberster-images-grid__text-over-image > h2 > strong',
                        {
                            top: 0,
                            duration: 0.5,
                        },
                        'text',
                    )
                    .to(
                        '.first-row-full-image > .cyberster-images-grid__text-over-image-container > .cyberster-images-grid__text-over-image > p',
                        {
                            opacity: 0.8,
                            duration: 0.5,
                        },
                    );
            }

            pictures.forEach((picture) => {
                picture.forEach((image) => {
                    const imageTimeline = gsap.timeline({
                        scrollTrigger: {
                            trigger: image,
                            start: 'top center',
                        },
                    });

                    imageTimeline
                        .to(image, {
                            '--image-overlay-top-position': '100%',
                            duration: 1,
                            ease: 'power4.out',
                        })
                        .add('text', 1)
                        .to(
                            '.first-row-full-image > .cyberster-images-grid__text-over-image-container > .cyberster-images-grid__text-over-image > h2 > strong',
                            {
                                top: 0,
                                duration: 0.5,
                            },
                            'text',
                        )
                        .to(
                            '.first-row-full-image > .cyberster-images-grid__text-over-image-container > .cyberster-images-grid__text-over-image > p',
                            {
                                opacity: 0.8,
                                duration: 0.5,
                            },
                        );
                });
            });
        },

        animateImageWithText() {
            const titleTimeline = gsap.timeline({
                scrollTrigger: {
                    trigger: '.cyberster-image-with-text__image-container',
                    start: 'top bottom',
                },
            });
            titleTimeline
                .to(
                    '.cyberster-block__title--image-with-text > p:last-of-type',
                    {
                        opacity: 1,
                        duration: 0.5,
                    },
                )
                .to('.cyberster-block__title--image-with-text > h2 > strong', {
                    top: 0,
                    duration: 0.5,
                });

            // This is the yOffset and yOffsetMultiplier values are been set by trial and error.
            const yOffset = this.isMobile ? 120 : 230;
            const yOffsetMultiplier = this.isMobile ? 1.5 : 2.2;
            const list = document.querySelector(
                '.cyberster-image-with-text__list > ul',
            );
            const listItemsLength =
                list && list.childElementCount ? list.childElementCount : 0;

            if (!list) return;

            gsap.set(list, { y: yOffset });
            gsap.to(list, {
                y: listItemsLength * -yOffset + yOffset * yOffsetMultiplier,
                ease: 'none',
                scrollTrigger: {
                    start: 'top 80px',
                    end: `bottom+=${Math.max(
                        listItemsLength * 100,
                        500,
                    )} bottom`,
                    trigger: '.cyberster-image-with-text-pin-wrapper',
                    pin: '.cyberster-image-with-text',
                    pinSpacing: true,
                    anticipatePin: 1,
                    scrub: true,
                    invalidateOnRefresh: true,
                },
            });
        },

        animateSecondImageGrid() {
            const pictures = [];
            const imagesParent = document.getElementsByClassName(
                'first-row-uneven-split',
            );
            if (!imagesParent.length) return;

            for (const parent of imagesParent) {
                pictures.push(parent.querySelectorAll(':scope > picture'));
            }

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: '.cyberster-first-row-uneven-split',
                    start: 'top bottom',
                },
            });
            timeline
                .to(
                    '.cyberster-first-row-uneven-split > .cyberster-block__title-container > .cyberster-block__title > p:first-of-type',
                    {
                        opacity: 1,
                        duration: 0.5,
                    },
                )
                .to(
                    '.cyberster-first-row-uneven-split > .cyberster-block__title-container > .cyberster-block__title > h2 > strong',
                    {
                        top: 0,
                        duration: 0.5,
                    },
                )
                .to(
                    '.cyberster-first-row-uneven-split > .cyberster-block__title-container > .cyberster-block__title > p:last-of-type',
                    {
                        opacity: 0.8,
                        duration: 0.5,
                    },
                );

            pictures.forEach((picture) => {
                picture.forEach((image) => {
                    const imageTimeline = gsap.timeline({
                        scrollTrigger: {
                            trigger: image,
                            start: 'top center',
                        },
                    });

                    imageTimeline.to(image, {
                        '--image-overlay-top-position': '100%',
                        duration: 1,
                        ease: 'power4.out',
                    });
                });
            });
        },

        animateThirdImageGrid() {
            const pictures = [];
            const imagesParent = document.getElementsByClassName(
                'first-row-even-split',
            );
            if (!imagesParent.length) return;

            for (const parent of imagesParent) {
                pictures.push(parent.querySelectorAll(':scope > picture'));
            }

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: '.cyberster-first-row-even-split',
                    start: 'top bottom',
                },
            });

            timeline
                .to(
                    '.cyberster-first-row-even-split > .cyberster-block__title-container > .cyberster-block__title > p:first-of-type',
                    {
                        opacity: 1,
                        duration: 0.5,
                    },
                )
                .to(
                    '.cyberster-first-row-even-split > .cyberster-block__title-container > .cyberster-block__title > h2 > strong',
                    {
                        top: 0,
                        duration: 0.5,
                    },
                )
                .to(
                    '.cyberster-first-row-even-split > .cyberster-block__title-container > .cyberster-block__title > p:last-of-type',
                    {
                        opacity: 0.8,
                        duration: 0.5,
                    },
                );

            pictures.forEach((picture) => {
                picture.forEach((image) => {
                    const imageTimeline = gsap.timeline({
                        scrollTrigger: {
                            trigger: image,
                            start: 'top center',
                        },
                    });

                    imageTimeline.to(image, {
                        '--image-overlay-top-position': '100%',
                        duration: 1,
                        ease: 'power4.out',
                    });
                });
            });
        },

        animateSliderSectionWithFeatures() {
            const sliderSectionsWithFeatures = document.querySelectorAll(
                '.slider-section-with-featuers--dark',
            );
            if (!sliderSectionsWithFeatures.length) return;

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: '.slider-section-with-featuers--dark',
                    start: 'top bottom',
                },
            });
            timeline
                .to('.slider-section-with-features__header__label', {
                    opacity: 1,
                    duration: 0.5,
                })
                .to('.slider-section-with-features__header__title > strong', {
                    top: 0,
                    duration: 0.5,
                })
                .to('.slider-section-with-features__header__subtitle', {
                    opacity: 0.8,
                    duration: 0.5,
                });
        },
    },
});
